<template>
  <div class="invoice-pdf-page">
    <div class="invoice-header">
      <div class="header-text">
        <span class="header-eyebrow">
          <i class="fa-brands fa-shopify"></i> Shopify · Facturación
        </span>
        <h2 class="header-title">Generar facturas</h2>
        <p class="header-sub">
          Descarga la factura PDF de un pedido individual o exporta el listado anual de España en CSV.
        </p>
      </div>
    </div>

    <div class="cards-grid">
      <b-card class="invoice-card" no-body>
        <div class="card-head">
          <div class="card-icon icon-primary">
            <i class="fa-solid fa-file-invoice"></i>
          </div>
          <div>
            <h5 class="card-title">Factura individual</h5>
            <p class="card-subtitle">PDF de un pedido concreto</p>
          </div>
        </div>

        <div class="card-body-inner">
          <div class="source-toggle">
            <button
              type="button"
              :class="['source-btn', { active: source === 'shopify' }]"
              @click="source = 'shopify'"
              :disabled="!!loading"
            >
              <i class="fa-brands fa-shopify"></i> Shopify
            </button>
            <button
              type="button"
              :class="['source-btn', { active: source === 'amazon' }]"
              @click="source = 'amazon'"
              :disabled="!!loading"
            >
              <i class="fa-brands fa-amazon"></i> Amazon
            </button>
          </div>

          <p class="hint" v-if="source === 'shopify'">
            Introduce el número de orden (<code>83490</code> o <code>#83490</code>) o el ID interno de Shopify.
          </p>
          <p class="hint" v-else>
            Introduce el ID del pedido de Amazon (<code>026-1234567-1234567</code>).
            Amazon no devuelve los datos del cliente, así que rellénalos abajo.
          </p>

          <div class="form-row">
            <b-form-input
              v-model="identifier"
              :placeholder="source === 'amazon' ? 'ID pedido Amazon (026-...)' : 'Número de orden o ID Shopify'"
              @keyup.enter="descargarFactura('pdf')"
              :disabled="!!loading"
            />
            <b-button
              variant="primary"
              class="action-btn"
              @click="descargarFactura('pdf')"
              :disabled="!!loading || !identifier"
            >
              <i class="fa-solid fa-file-pdf" v-if="loading !== 'pdf'"></i>
              <b-spinner small v-else></b-spinner>
              <span>{{ loading === 'pdf' ? 'Generando...' : 'Descargar PDF' }}</span>
            </b-button>
          </div>

          <b-form-checkbox
            v-if="source === 'shopify'"
            v-model="sinRefunds"
            class="mt-50"
            :disabled="!!loading"
          >
            No incluir productos reembolsados (refunds)
          </b-form-checkbox>

          <div class="extra-toggle">
            <a href="#" @click.prevent="mostrarDatos = !mostrarDatos">
              <i :class="mostrarDatos ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
              {{ mostrarDatos ? 'Ocultar datos de facturación' : 'Agregar datos de facturación (CIF, dirección)' }}
            </a>
          </div>

          <transition name="fade">
            <div v-if="mostrarDatos" class="billing-fields">
              <div class="bf-row">
                <label>Razón social / Nombre</label>
                <b-form-input v-model="billing.name" placeholder="Empresa S.L." :disabled="!!loading" />
              </div>
              <div class="bf-grid">
                <div class="bf-row">
                  <label>CIF / NIF</label>
                  <b-form-input v-model="billing.nif" placeholder="B12345678" :disabled="!!loading" />
                </div>
                <div class="bf-row">
                  <label>IVA / Tax (%)</label>
                  <b-form-input
                    v-model="billing.taxRate"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="21"
                    :disabled="!!loading"
                  />
                </div>
              </div>
              <div class="bf-row">
                <label>Dirección</label>
                <b-form-input v-model="billing.address1" placeholder="Calle, número" :disabled="!!loading" />
              </div>
              <div class="bf-grid">
                <div class="bf-row">
                  <label>Ciudad</label>
                  <b-form-input v-model="billing.city" :disabled="!!loading" />
                </div>
                <div class="bf-row">
                  <label>Código postal</label>
                  <b-form-input v-model="billing.zip" :disabled="!!loading" />
                </div>
              </div>
              <div class="bf-grid">
                <div class="bf-row">
                  <label>Provincia</label>
                  <b-form-input v-model="billing.province" :disabled="!!loading" />
                </div>
                <div class="bf-row">
                  <label>País</label>
                  <b-form-input v-model="billing.country" :disabled="!!loading" />
                </div>
              </div>
              <p class="bf-note">
                <i class="fa-solid fa-circle-info"></i>
                Cada campo que rellenes sustituye al de Shopify; los que dejes vacíos se mantienen de Shopify. El CIF/NIF se añade a la factura.
                Si indicas un % de IVA, se desglosa el impuesto sobre el total (IVA incluido): el Total no cambia y el subtotal/líneas pasan a neto.
              </p>

              <template v-if="source === 'amazon'">
                <div class="bf-divider">
                  <span>Envío (Ship to) — opcional</span>
                </div>
                <div class="bf-row">
                  <label>Nombre</label>
                  <b-form-input v-model="shipping.name" placeholder="Nombre destinatario" :disabled="!!loading" />
                </div>
                <div class="bf-row">
                  <label>Dirección</label>
                  <b-form-input v-model="shipping.address1" placeholder="Calle, número" :disabled="!!loading" />
                </div>
                <div class="bf-grid">
                  <div class="bf-row">
                    <label>Ciudad</label>
                    <b-form-input v-model="shipping.city" :disabled="!!loading" />
                  </div>
                  <div class="bf-row">
                    <label>Código postal</label>
                    <b-form-input v-model="shipping.zip" :disabled="!!loading" />
                  </div>
                </div>
                <div class="bf-grid">
                  <div class="bf-row">
                    <label>Provincia</label>
                    <b-form-input v-model="shipping.province" :disabled="!!loading" />
                  </div>
                  <div class="bf-row">
                    <label>País</label>
                    <b-form-input v-model="shipping.country" :disabled="!!loading" />
                  </div>
                </div>
                <p class="bf-note">
                  <i class="fa-solid fa-circle-info"></i>
                  Si dejas el envío vacío, la sección "Ship to" no aparecerá en la factura.
                </p>
              </template>
            </div>
          </transition>

          <transition name="fade">
            <div v-if="error" class="error-msg">
              <i class="fa-solid fa-circle-exclamation"></i> {{ error }}
            </div>
          </transition>
        </div>
      </b-card>

      <b-card class="invoice-card" no-body>
        <div class="card-head">
          <div class="card-icon icon-warning">
            <i class="fa-solid fa-truck-fast"></i>
          </div>
          <div>
            <h5 class="card-title">Factura comercial UPS</h5>
            <p class="card-subtitle">Formato aduanas que pide UPS</p>
          </div>
        </div>

        <div class="card-body-inner">
          <p class="hint">
            Introduce el número de orden de Shopify (<code>83490</code>) o su ID interno.
            Los datos del envío se rellenan solos; puedes sobrescribirlos abajo.
          </p>

          <div class="form-row">
            <b-form-input
              v-model="upsIdentifier"
              placeholder="Número de orden o ID Shopify"
              @keyup.enter="descargarComercialUps"
              :disabled="loadingUps"
            />
            <b-button
              variant="warning"
              class="action-btn"
              @click="descargarComercialUps"
              :disabled="loadingUps || !upsIdentifier"
            >
              <i class="fa-solid fa-file-pdf" v-if="!loadingUps"></i>
              <b-spinner small v-else></b-spinner>
              <span>{{ loadingUps ? 'Generando...' : 'Descargar PDF' }}</span>
            </b-button>
          </div>

          <b-form-checkbox
            v-model="upsDevolucion"
            class="mt-50"
            :disabled="loadingUps"
          >
            Devolución (el cliente nos envía: invierte remitente y destinatario, Reason = RETURN)
          </b-form-checkbox>

          <div class="extra-toggle">
            <a href="#" @click.prevent="mostrarUps = !mostrarUps">
              <i :class="mostrarUps ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
              {{ mostrarUps ? 'Ocultar datos del envío' : 'Editar datos del envío (waybill, peso, HS code)' }}
            </a>
          </div>

          <transition name="fade">
            <div v-if="mostrarUps" class="billing-fields">
              <div class="bf-grid">
                <div class="bf-row">
                  <label>Waybill Number</label>
                  <b-form-input v-model="ups.waybill" placeholder="Auto: tracking del pedido" :disabled="loadingUps" />
                </div>
                <div class="bf-row">
                  <label>Shipment ID</label>
                  <b-form-input v-model="ups.shipmentId" placeholder="K459E64QB84" :disabled="loadingUps" />
                </div>
              </div>
              <div class="bf-grid">
                <div class="bf-row">
                  <label>Nº de bultos</label>
                  <b-form-input v-model="ups.packages" type="number" min="1" placeholder="1" :disabled="loadingUps" />
                </div>
                <div class="bf-row">
                  <label>Peso total (kg)</label>
                  <b-form-input v-model="ups.weight" type="number" min="0" step="0.1" placeholder="Auto: peso del pedido" :disabled="loadingUps" />
                </div>
              </div>
              <div class="bf-grid">
                <div class="bf-row">
                  <label>Descripción mercancía</label>
                  <b-form-input v-model="ups.description" placeholder="LEATHER GOODS" :disabled="loadingUps" />
                </div>
                <div class="bf-row">
                  <label>Harm. Code</label>
                  <b-form-input v-model="ups.harmCode" placeholder="4202310010" :disabled="loadingUps" />
                </div>
              </div>
              <div class="bf-grid">
                <div class="bf-row">
                  <label>Incoterm</label>
                  <b-form-input v-model="ups.incoterm" placeholder="DAP" :disabled="loadingUps" />
                </div>
                <div class="bf-row">
                  <label>País de origen (C/O)</label>
                  <b-form-input v-model="ups.origin" placeholder="ES" :disabled="loadingUps" />
                </div>
              </div>
              <p class="bf-note">
                <i class="fa-solid fa-circle-info"></i>
                Todo es opcional: lo que dejes vacío se rellena solo (waybill del tracking del pedido, peso y unidades de las líneas) o usa el valor por defecto.
              </p>
            </div>
          </transition>

          <transition name="fade">
            <div v-if="errorUps" class="error-msg">
              <i class="fa-solid fa-circle-exclamation"></i> {{ errorUps }}
            </div>
          </transition>
        </div>
      </b-card>

      <b-card class="invoice-card" no-body>
        <div class="card-head">
          <div class="card-icon icon-success">
            <i class="fa-solid fa-file-csv"></i>
          </div>
          <div>
            <h5 class="card-title">Exportar año (España)</h5>
            <p class="card-subtitle">CSV con todas las facturas ES</p>
          </div>
        </div>

        <div class="card-body-inner">
          <p class="hint">
            Descarga un CSV con todas las facturas de España (país = ES) del año seleccionado.
            Puede tardar unos segundos según el volumen.
          </p>

          <div class="form-row">
            <b-form-input
              v-model.number="year"
              type="number"
              min="2021"
              :max="currentYear"
              placeholder="Año"
              @keyup.enter="descargarAnoEspana"
              :disabled="loadingYear"
            />
            <b-button
              variant="success"
              class="action-btn"
              @click="descargarAnoEspana"
              :disabled="loadingYear || !year"
            >
              <i class="fa-solid fa-file-csv" v-if="!loadingYear"></i>
              <b-spinner small v-else></b-spinner>
              <span>{{ loadingYear ? 'Descargando...' : 'Descargar CSV' }}</span>
            </b-button>
          </div>

          <transition name="fade">
            <div v-if="errorYear" class="error-msg">
              <i class="fa-solid fa-circle-exclamation"></i> {{ errorYear }}
            </div>
          </transition>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import axios from "@axios";
import {
  BCard,
  BFormInput,
  BButton,
  BSpinner,
  BFormCheckbox,
} from "bootstrap-vue";

export default {
  components: {
    BCard,
    BFormInput,
    BButton,
    BSpinner,
    BFormCheckbox,
  },
  data() {
    return {
      source: "shopify",
      identifier: "",
      sinRefunds: false,
      loading: false,
      error: "",
      year: new Date().getFullYear(),
      loadingYear: false,
      errorYear: "",
      mostrarDatos: false,
      mostrarUps: false,
      upsIdentifier: "",
      upsDevolucion: false,
      loadingUps: false,
      errorUps: "",
      ups: {
        waybill: "",
        shipmentId: "",
        packages: "",
        weight: "",
        description: "",
        harmCode: "",
        incoterm: "",
        origin: "",
      },
      billing: {
        name: "",
        nif: "",
        taxRate: "",
        address1: "",
        city: "",
        zip: "",
        province: "",
        country: "",
      },
      shipping: {
        name: "",
        address1: "",
        city: "",
        zip: "",
        province: "",
        country: "",
      },
    };
  },
  computed: {
    currentYear() {
      return new Date().getFullYear();
    },
  },
  watch: {
    source(val) {
      this.error = "";
      // Amazon no devuelve cliente: abrimos los campos de facturación.
      if (val === "amazon") this.mostrarDatos = true;
    },
  },
  methods: {
    async descargarFactura(formato) {
      this.error = "";
      const id = String(this.identifier || "").trim();
      if (!id) return;

      this.loading = formato;
      try {
        const isAmazon = this.source === "amazon";
        const cleanId = isAmazon ? id : id.replace("#", "");
        const mime = formato === "csv" ? "text/csv" : "application/pdf";
        const ext = formato === "csv" ? "csv" : "pdf";

        // Amazon solo genera PDF; el CSV anual sigue siendo de Shopify.
        let reqUrl;
        if (isAmazon) {
          reqUrl = `/amazon/invoice/${encodeURIComponent(cleanId)}`;
        } else {
          const endpoint = formato === "csv" ? "invoice-csv" : "invoice";
          reqUrl = `/shopify/${endpoint}/${encodeURIComponent(cleanId)}`;
        }

        const params = {};
        if (formato !== "csv") {
          if (!isAmazon && this.sinRefunds) params.exclude_refunded = 1;

          const b = this.billing;
          if (b.name && b.name.trim()) params.bill_name = b.name.trim();
          if (b.nif && b.nif.trim()) params.bill_nif = b.nif.trim();
          if (b.taxRate !== "" && b.taxRate !== null && Number(b.taxRate) > 0) {
            params.bill_tax_rate = b.taxRate;
          }
          if (b.address1 && b.address1.trim()) params.bill_address1 = b.address1.trim();
          if (b.city && b.city.trim()) params.bill_city = b.city.trim();
          if (b.zip && b.zip.trim()) params.bill_zip = b.zip.trim();
          if (b.province && b.province.trim()) params.bill_province = b.province.trim();
          if (b.country && b.country.trim()) params.bill_country = b.country.trim();

          // Ship to opcional, solo aplica a Amazon (Shopify ya trae su envío).
          if (isAmazon) {
            const s = this.shipping;
            if (s.name && s.name.trim()) params.ship_name = s.name.trim();
            if (s.address1 && s.address1.trim()) params.ship_address1 = s.address1.trim();
            if (s.city && s.city.trim()) params.ship_city = s.city.trim();
            if (s.zip && s.zip.trim()) params.ship_zip = s.zip.trim();
            if (s.province && s.province.trim()) params.ship_province = s.province.trim();
            if (s.country && s.country.trim()) params.ship_country = s.country.trim();
          }
        }

        const response = await axios.get(reqUrl, {
          responseType: "blob",
          params,
        });

        const blob = new Blob([response.data], { type: mime });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `invoice-${cleanId}.${ext}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (e) {
        if (e.response && e.response.status === 404) {
          this.error = this.source === "amazon"
            ? "Pedido no encontrado en Amazon."
            : "Orden no encontrada en Shopify.";
        } else if (e.response && e.response.data) {
          try {
            const text = await e.response.data.text();
            const json = JSON.parse(text);
            this.error = json.error || "Error generando la factura.";
          } catch (_) {
            this.error = "Error generando la factura.";
          }
        } else {
          this.error = "Error de conexión.";
        }
      } finally {
        this.loading = false;
      }
    },
    // Factura comercial en el formato que pide UPS (solo Shopify).
    async descargarComercialUps() {
      this.errorUps = "";
      const id = String(this.upsIdentifier || "").trim().replace("#", "");
      if (!id) return;

      this.loadingUps = true;
      try {
        const map = {
          waybill: "waybill",
          shipmentId: "shipment_id",
          packages: "packages",
          weight: "weight",
          description: "description",
          harmCode: "harm_code",
          incoterm: "incoterm",
          origin: "origin",
        };
        const params = {};
        Object.keys(map).forEach((k) => {
          const v = String(this.ups[k] == null ? "" : this.ups[k]).trim();
          if (v) params[map[k]] = v;
        });
        if (this.upsDevolucion) params.return = 1;

        const response = await axios.get(
          `/shopify/commercial-invoice/${encodeURIComponent(id)}`,
          { responseType: "blob", params }
        );

        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `commercial-invoice-${id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (e) {
        if (e.response && e.response.status === 404) {
          this.errorUps = "Orden no encontrada en Shopify.";
        } else if (e.response && e.response.data) {
          try {
            const text = await e.response.data.text();
            const json = JSON.parse(text);
            this.errorUps = json.error || "Error generando la factura comercial.";
          } catch (_) {
            this.errorUps = "Error generando la factura comercial.";
          }
        } else {
          this.errorUps = "Error de conexión.";
        }
      } finally {
        this.loadingUps = false;
      }
    },
    async descargarAnoEspana() {
      this.errorYear = "";
      const y = parseInt(this.year, 10);
      if (!y || y < 2000 || y > this.currentYear) {
        this.errorYear = "Año inválido.";
        return;
      }

      this.loadingYear = true;
      try {
        const response = await axios.get(`/shopify/invoices-csv-year/${y}`, {
          responseType: "blob",
          timeout: 600000,
        });

        const blob = new Blob([response.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `invoices-ES-${y}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (e) {
        if (e.response && e.response.status === 404) {
          this.errorYear = `No hay órdenes de España en ${y}.`;
        } else if (e.response && e.response.data) {
          try {
            const text = await e.response.data.text();
            const json = JSON.parse(text);
            this.errorYear = json.error || "Error generando el CSV.";
          } catch (_) {
            this.errorYear = "Error generando el CSV.";
          }
        } else {
          this.errorYear = "Error de conexión.";
        }
      } finally {
        this.loadingYear = false;
      }
    },
  },
};
</script>

<style scoped>
.invoice-pdf-page {
  padding: 8px 4px 32px;
}

/* Header */
.invoice-header {
  margin-bottom: 24px;
}
.header-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #7367f0;
  background: rgba(115, 103, 240, 0.1);
  padding: 4px 10px;
  border-radius: 20px;
}
.header-eyebrow i {
  font-size: 13px;
}

.header-title {
  margin: 10px 0 4px;
  font-size: 26px;
  font-weight: 600;
  color: #5e5873;
}
.header-sub {
  margin: 0;
  font-size: 14px;
  color: #6e6b7b;
  max-width: 720px;
}

/* Grid */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  align-items: start;
  gap: 20px;
  max-width: 1600px;
}

/* Card */
.invoice-card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(34, 41, 47, 0.06);
  overflow: hidden;
}
.invoice-card >>> .card-body {
  padding: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* Card head */
.card-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px;
  border-bottom: 1px solid #ebe9f1;
  background: linear-gradient(135deg, #fafbfc 0%, #ffffff 100%);
}
.card-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}
.card-icon.icon-primary {
  background: rgba(115, 103, 240, 0.12);
  color: #7367f0;
}
.card-icon.icon-warning {
  background: rgba(255, 159, 67, 0.12);
  color: #ff9f43;
}
.card-icon.icon-success {
  background: rgba(40, 199, 111, 0.12);
  color: #28c76f;
}
.card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #5e5873;
}
.card-subtitle {
  margin: 2px 0 0;
  font-size: 12px;
  color: #b9b9c3;
}

/* Card body */
.card-body-inner {
  padding: 20px 24px 24px;
  display: flex;
  flex-direction: column;
  flex: 1;
}
.hint {
  font-size: 13px;
  color: #6e6b7b;
  margin-bottom: 16px;
  line-height: 1.5;
  min-height: 40px;
}
.form-row {
  margin-top: auto;
}
.hint code {
  background: #f1f1f4;
  color: #7367f0;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

/* Source toggle (Shopify / Amazon) */
.source-toggle {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  margin-bottom: 16px;
  background: #f1f1f4;
  border-radius: 10px;
}
.source-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  color: #6e6b7b;
  font-size: 13px;
  font-weight: 500;
  padding: 7px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}
.source-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
.source-btn.active {
  background: #fff;
  color: #5e5873;
  box-shadow: 0 2px 6px rgba(34, 41, 47, 0.08);
}

/* Form */
.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: stretch;
}
.form-row >>> .form-control {
  flex: 1;
  height: 42px;
  border-radius: 8px;
  border-color: #d8d6de;
  font-size: 14px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.form-row >>> .form-control:focus {
  border-color: #7367f0;
  box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.15);
}
.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 42px;
  padding: 0 18px;
  border-radius: 8px;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 4px 10px rgba(115, 103, 240, 0.15);
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.action-btn:hover:not(:disabled) {
  transform: translateY(-1px);
}
.action-btn.btn-success {
  box-shadow: 0 4px 10px rgba(40, 199, 111, 0.18);
}

/* Datos de facturación extra */
.extra-toggle {
  margin-top: 14px;
}
.extra-toggle a {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #7367f0;
  text-decoration: none;
}
.extra-toggle a:hover {
  text-decoration: underline;
}
.billing-fields {
  margin-top: 14px;
  padding: 16px;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  background: #fafbfc;
}
.bf-row {
  margin-bottom: 12px;
}
.bf-row:last-child {
  margin-bottom: 0;
}
.bf-row label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #6e6b7b;
  margin-bottom: 4px;
}
.billing-fields >>> .form-control {
  height: 38px;
  border-radius: 8px;
  border-color: #d8d6de;
  font-size: 14px;
}
.billing-fields >>> .form-control:focus {
  border-color: #7367f0;
  box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.15);
}
.bf-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.bf-divider {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 18px 0 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #a39fb3;
}
.bf-divider::before,
.bf-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #ebe9f1;
}
.bf-note {
  margin: 4px 0 0;
  font-size: 12px;
  color: #b9b9c3;
  line-height: 1.4;
}
.bf-note i {
  color: #7367f0;
}

/* Error */
.error-msg {
  margin-top: 16px;
  padding: 11px 14px;
  background: rgba(234, 84, 85, 0.1);
  color: #ea5455;
  border-left: 3px solid #ea5455;
  border-radius: 6px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 640px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
  .form-row {
    flex-direction: column;
  }
  .action-btn {
    justify-content: center;
    width: 100%;
  }
}

/* Dark theme */
.dark-layout .header-title {
  color: #d0d2d6;
}
.dark-layout .header-sub,
.dark-layout .hint {
  color: #b4b7bd;
}
.dark-layout .card-title {
  color: #d0d2d6;
}
.dark-layout .card-head {
  background: linear-gradient(135deg, #283046 0%, #2c3149 100%);
  border-bottom-color: #3b4253;
}
.dark-layout .invoice-card {
  background: #283046;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
}
.dark-layout .hint code {
  background: #3b4253;
  color: #a78bfa;
}
.dark-layout .source-toggle {
  background: #242b3d;
}
.dark-layout .source-btn {
  color: #b4b7bd;
}
.dark-layout .source-btn.active {
  background: #283046;
  color: #d0d2d6;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}
.dark-layout .form-row >>> .form-control {
  background: #283046;
  border-color: #404656;
  color: #d0d2d6;
}
.dark-layout .billing-fields {
  background: #242b3d;
  border-color: #3b4253;
}
.dark-layout .bf-row label {
  color: #b4b7bd;
}
.dark-layout .billing-fields >>> .form-control {
  background: #283046;
  border-color: #404656;
  color: #d0d2d6;
}
.dark-layout .bf-divider::before,
.dark-layout .bf-divider::after {
  background: #3b4253;
}
</style>
